import type {
  PartnerLabelAugmentationCreate,
  PartnerLabelAugmentationUpdate,
  PartnerLabelAugmentationV2,
} from '@shipium-corp/shipium-ts-types';
import GatewayAxiosClient from '~/store/services/GatewayAxiosClient';
import type { LinkAssetPayload, BulkLinkPayloads } from '../api-utils';
import { linkMultipleAssets } from '../api-utils';

type SingleAssetLinkType = '';
type ArrayAssetLinkType = 'tags';

export const labelAugmentations = {
  search: {
    queryKey: 'partner-management-service/label-augmentations',
    configurationType: 'default' as const,
    fn: async () => {
      const { data } = await GatewayAxiosClient.get<PartnerLabelAugmentationV2[]>(
        `/partner-management-proxy/api/v1/partners/labelAugmentations`,
      );

      return data;
    },
  },
  get: {
    queryKey: 'partner-management-service/label-augmentations/id',
    configurationType: 'no-cache' as const,
    fn: async (labelAugmentationId: string) => {
      const { data } = await GatewayAxiosClient.get<PartnerLabelAugmentationV2>(
        `/partner-management-proxy/api/v1/partners/labelAugmentations/${labelAugmentationId}`,
      );

      return data;
    },
  },
  create: async (payload: PartnerLabelAugmentationCreate) => {
    const { data } = await GatewayAxiosClient.post<PartnerLabelAugmentationV2>(
      `/partner-management-proxy/api/v1/partners/labelAugmentations`,
      payload,
    );

    return {
      data,
      invalidateQueryKeys: [['partner-management-service/label-augmentations']],
    };
  },
  update: async ({
    labelAugmentationId,
    payload,
  }: {
    labelAugmentationId: string;
    payload: PartnerLabelAugmentationUpdate;
  }) => {
    const { data } = await GatewayAxiosClient.put<PartnerLabelAugmentationV2>(
      `/partner-management-proxy/api/v1/partners/labelAugmentations/${labelAugmentationId}`,
      payload,
    );

    return {
      data,
      invalidateQueryKeys: [
        ['partner-management-service/label-augmentations'],
        ['partner-management-service/label-augmentations/id', data.labelAugmentationId!],
      ],
    };
  },
  link: async ({
    labelAugmentationId,
    payload,
  }: {
    labelAugmentationId: string;
    payload: LinkAssetPayload<SingleAssetLinkType | ArrayAssetLinkType>;
  }) => {
    await GatewayAxiosClient({
      method: payload.linkAction === 'link' ? 'PUT' : 'DELETE',
      url:
        `/partner-management-proxy/api/v1/partners/labelAugmentations` +
        `/${labelAugmentationId}/${payload.assetType}/${payload.assetId}`,
    });

    return {
      invalidateQueryKeys: [
        ['partner-management-service/label-augmentations'],
        ['partner-management-service/label-augmentations/id', labelAugmentationId],
      ],
    };
  },
  bulkLink: async ({
    labelAugmentation,
    payloads,
  }: {
    labelAugmentation: PartnerLabelAugmentationV2;
    payloads: BulkLinkPayloads<SingleAssetLinkType, ArrayAssetLinkType>;
  }) => {
    return await linkMultipleAssets<SingleAssetLinkType, ArrayAssetLinkType, PartnerLabelAugmentationV2>(
      payloads,
      {
        tags: labelAugmentation.tagIds,
      },
      (linkPayload) =>
        labelAugmentations.link({
          labelAugmentationId: labelAugmentation.labelAugmentationId!,
          payload: linkPayload,
        }),
    );
  },
};
